import { Component, Input, inject } from "@angular/core";
import { AuditFindingLinkModel } from "@app/audit-finding/shared/data-models/audit-finding-link-model";
import { AuditDataService } from "@app/audit/services/audit-data.service";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { Pages } from "@constants/pages/Pages";
import { FormatType } from "@enums/FormatType";
import { RendererType } from "@enums/RendererType";
import { StandingDataType } from "@enums/StandingDataType";
import { nameof } from "@methods/jeffs-toolkit";
import { LinkedMoiModel } from "@models/moi/LinkedMoiModel";
import { Observable } from "rxjs";
import { TabService } from "./../../../../../../services/tabs/TabService";
import { injectTranslation } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/audit/i18n/scope";
import { ExpandableListComponent } from "@app/shared/components/expandable-list/expandable-list.component";

@Component({
    selector: "audit-detail-finding-linking",
    templateUrl: "./audit-detail-finding-linking.component.html",
    imports: [ExpandableListComponent],
})
export class AuditDetailFindingLinkingComponent {
    private _ds = inject(AuditDataService);
    private _sdDs = inject(StandingdataDataService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _translation = injectTranslation(scope);

    @Input() auditGuid: string;

    mainConfig: GenericListConfig;
    innerConfig: GenericListConfig;
    innerConfigDataMethod: (guid: string) => Observable<any>;

    async init() {
        // The list configs hold translated text.
        await this._translation.ready();
        if (!this.mainConfig) {
            this.initMainConfig();
            this.initInnerConfig();
        }
    }

    initMainConfig() {
        this.mainConfig = {
            name: this._translation.t("findings.linkedFindingsTitle"),
            idProp: "guid",

            allowAdd: false,
            allowDelete: false,
            allowEdit: false,

            fields: [
                {
                    prettyName: this._translation.t("findings.identifierColumn"),
                    fieldName: nameof<AuditFindingLinkModel>((x) => x.identifier),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this._translation.t("findings.findingNameColumn"),
                    fieldName: nameof<AuditFindingLinkModel>((x) => x.findingName),
                    fieldType: GenericListFieldType.Text,
                    hideInOverview: true,
                    hideInEdit: true,
                },
                {
                    prettyName: this._translation.t("findings.findingDescriptionColumn"),
                    fieldName: nameof<AuditFindingLinkModel>((x) => x.findingDescription),
                    fieldType: GenericListFieldType.TextArea,
                },
                {
                    prettyName: this._translation.t("findings.recommendationColumn"),
                    fieldName: nameof<AuditFindingLinkModel>((x) => x.recommendation),
                    fieldType: GenericListFieldType.TextArea,
                },
                {
                    prettyName: this._translation.t("findings.severityColumn"),
                    fieldName: nameof<AuditFindingLinkModel>((x) => x.severity),
                    fieldType: GenericListFieldType.ColoredSelect,
                    getDataMethod: () => {
                        return this._sdDs.getAllByType(StandingDataType.AuditSeverity);
                    },
                },
            ],

            dataMethod: () => {
                return this._ds.getAuditLinkedFindings(this.auditGuid);
            },
            overviewRowActions: [],
        };
        this.mainConfig.overviewRowActions.push({
            icon: "fas fa-external-link-alt",
            tooltip: this._translation.t("findings.openDetailsTooltip"),
            clickEvent: (row: AuditFindingLinkModel) => {
                this._tabService.generateTab(
                    this._pages.AuditFindingDetail,
                    row.guid,
                    `(A.F.) ${+row.identifier} - ${row.findingName}`
                );
            },
        });
    }

    initInnerConfig() {
        this.innerConfig = <GenericListConfig>{
            name: this._translation.t("findings.linkedMoisTitle"),
            idProp: "guid",

            allowAdd: false,
            allowEdit: false,
            allowLink: false,

            fields: [
                {
                    prettyName: this._translation.t("findings.identifierColumn"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.identifier),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this._translation.t("findings.nameColumn"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.name),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this._translation.t("findings.organizationResponsibleColumn"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.organizationResponsible),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this._translation.t("findings.responsibleColumn"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.responsible),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this._translation.t("findings.statusColumn"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.status),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this._translation.t("findings.dueDateColumn"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.dueDate),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    prettyName: this._translation.t("findings.progressColumn"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.progress),
                    fieldType: GenericListFieldType.Text,
                },
            ],

            rendererConfig: [
                {
                    textColumn: nameof<LinkedMoiModel>((x) => x.dueDate),
                    actionColumn: nameof<LinkedMoiModel>((x) => x.isOverdue),
                    actionValue: "Yes",
                    type: RendererType.DueDate,
                    formatType: FormatType.DateFormat,
                },
                {
                    textColumn: nameof<LinkedMoiModel>((x) => x.progress),
                    type: RendererType.Progress,
                },
            ],
            overviewRowActions: [],
        };
        this.innerConfig.overviewRowActions.push({
            icon: "fas fa-external-link-alt",
            tooltip: this._translation.t("findings.openDetailsTooltip"),
            clickEvent: (row: LinkedMoiModel) => {
                this._tabService.generateTab(this._pages.MoiDetail, row.guid);
            },
        });
        this.innerConfigDataMethod = this._ds.getLinkedFindingsMois.bind(this._ds);
    }
}
